import { useCallback, useState } from 'react';
import {
    PublicSessionFileType,
    uploadSessionShareFile,
    UploadSessionShareFileResponse,
} from '../../../api/publicSessionApi';
import logger from '../../../utils/logger';

export type UploadStatus = 'idle' | 'uploading' | 'done' | 'error';

export interface UploadEntry {
    id: string;
    file: File;
    fileType: PublicSessionFileType;
    status: UploadStatus;
    progress: number;
    error?: string;
    response?: UploadSessionShareFileResponse;
}

export interface PublicShareUploadsState {
    uploads: UploadEntry[];
    isAnyUploading: boolean;
    addAndUpload: (file: File, fileType: PublicSessionFileType) => Promise<UploadEntry>;
    reset: () => void;
}

/**
 * Tracks per-file upload state for the public-share form. Each `addAndUpload`
 * call appends a new entry, fires the multipart POST, threads progress events
 * back into the entry, and resolves with the final entry once the backend
 * confirms (or rejects). Caller decides whether to gate later steps (AI
 * prefill) on `uploads.every(u => u.status === 'done')`.
 */
export const usePublicShareUploads = (
    publicShareId: string | undefined,
): PublicShareUploadsState => {
    const [uploads, setUploads] = useState<UploadEntry[]>([]);

    const addAndUpload = useCallback(
        async (file: File, fileType: PublicSessionFileType): Promise<UploadEntry> => {
            if (!publicShareId) {
                const broken: UploadEntry = {
                    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    file,
                    fileType,
                    status: 'error',
                    progress: 0,
                    error: 'Missing share identifier.',
                };
                setUploads((prev) => [...prev, broken]);
                return broken;
            }

            const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const entry: UploadEntry = {
                id,
                file,
                fileType,
                status: 'uploading',
                progress: 0,
            };
            setUploads((prev) => [...prev, entry]);

            try {
                const response = await uploadSessionShareFile(publicShareId, file, fileType, {
                    onProgress: (percent) => {
                        setUploads((prev) =>
                            prev.map((u) => (u.id === id ? { ...u, progress: percent } : u)),
                        );
                    },
                });
                const done: UploadEntry = {
                    ...entry,
                    status: 'done',
                    progress: 100,
                    response,
                };
                setUploads((prev) => prev.map((u) => (u.id === id ? done : u)));
                return done;
            } catch (err: any) {
                logger.error('Public-share upload failed', err);
                const message =
                    err?.response?.data?.message ||
                    err?.message ||
                    'Upload failed. Please try a different file or contact your recruiter.';
                const failed: UploadEntry = {
                    ...entry,
                    status: 'error',
                    error: message,
                };
                setUploads((prev) => prev.map((u) => (u.id === id ? failed : u)));
                return failed;
            }
        },
        [publicShareId],
    );

    const reset = useCallback(() => setUploads([]), []);

    const isAnyUploading = uploads.some((u) => u.status === 'uploading');

    return { uploads, isAnyUploading, addAndUpload, reset };
};
